import Editor from "@monaco-editor/react";
import { IScheduledTaskHistoryDTO, ScheduledTasksApi } from "api/ScheduledTasksApi";
import { useUpdateTask } from "api/api-hooks/airflow-hooks";
import { ScheduledTaskDTO } from "api/model/airflow-task/ScheduledTaskDTO";
import { TaskInputGroup } from "api/model/airflow-task/TaskInputGroup";
import {
  ITaskTemplateInputDefinition,
  TaskTemplateDefinitionDTO,
  TaskTemplateInputGroupDefinition,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { TaskTemplateInputGroupType, TaskTemplateInputType, TaskTemplatingType } from "api/model/enums";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import JSONEditor from "components/basic/json-editor/JSONEditor";
import CreateDatasetDialog from "components/dashboard/dataset-window/datasets-menu/create-dataset-dialog/CreateDatasetDialog";
import InfoLabel from "components/pages/scheduled-task-airflow/components/InfoLabel";
import TaskConfigConstantMapTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigConstantMapTable";
import TaskConfigIndexedDatasetTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigIndexedDatasetTable";
import TaskConfigNamedDatasetTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigNamedDatasetTable";
import TaskConfigProductTemplatesTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigProductTemplatesTable";
import EasasPageTemplateWrapper from "components/templates/EasasPageTemplateWrapper";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Menu } from "primereact/menu";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { T } from "services/lang/LanguageService";
import { taskExecutorQueues } from "utils/constants";
import { JSONEditorMode } from "utils/enums";
import { fmtDatetime } from "utils/time-utils";
import ScheduledTaskHistoryTable from "../../basic/tables/scheduled-task-history/ScheduledTaskHistoryTable";
import UserAvatar from "../../basic/user-avatar/UserAvatar";
import { easasAlert } from "../../fragments/easas-global-fragment/EasasGlobalAlerts";
import { errorToaster, warningToaster } from "../measurement-devices/utils/utils";
import "./AirflowTaskConfigurator.scss";
import InputComment from "./components/InputComment/InputComment";
import {
  BooleanFieldInput,
  ChipsFieldInput,
  CodeUploadInput,
  DatasetCategoryDropdownField,
  DatasetDropdownFieldInput,
  DatasetMultiSelectFieldInput,
  DropdownFieldInput,
  IFieldInputProps,
  InputChangeEvent,
  IPythonCodeFile,
  NumberFieldInput,
  TaskConfigConstantField,
  TaskConfigFreestyleJsonField,
  TaskConfigProductTemplateField,
  TaskDependencyDropdown,
  TextFieldInput,
  TimespanFieldInput,
} from "./components/InputFields";
import TaskDataDownloadButton from "./components/TaskDataDownloadButton";
import TemplateInfoDialog from "./components/TemplateInfoDialog";
import TemplateUpdateDialog from "./components/TemplateUpdateDialog";
import TaskConfigCategoryTable from "./components/tables/TaskConfigCategoryTable";
import TaskConfigDatasetPairTable from "./components/tables/TaskConfigDatasetPairTable";
import TaskTemplatesSubgroupManagerTable from "./components/tables/TaskTemplatesSubgroupManagerTable";
import TemplateEditor from "./components/templateEditor/TemplateEditor";
import { useDisplayTimezone } from "hooks/useTimezone";
import airflowLogo from "img/airflow-icon16.png";

interface IItem {
  index: number;
  label: string;
  subGroups: TaskInputGroup[];
  type: string;
  value: string;
}

interface CustomListItemProps {
  item: IItem;
  index: number;
  setSelectedTemplateIndex: (e: number | null) => void;
  setActiveTabIndex: (e: number) => void;
  setTabIndex: (e: number) => void;
  setSelectedSubGroupIndex: (e: number | null) => void;
}

export default function AirflowTaskConfigurator() {
  const [taskCfg, setTaskCfg] = useState<ScheduledTaskDTO>(new ScheduledTaskDTO());
  const [taskCfgLoading, setTaskCfgLoading] = useState<boolean>(true);
  const [selectedTaskHistory, setSelectedTaskHistory] = useState<IScheduledTaskHistoryDTO>();
  const [showRollbackDialog, setShowRollbackDialog] = useState<boolean>(false);
  const [showTemplateInfoDialog, setShowTemplateInfoDialog] = useState<boolean>(false);
  const [showTemplateUpdateDialog, setShowTemplateUpdateDialog] = useState<boolean>(false);
  const [createDatasetDialogVisibility, setCreateDialogVisibility] = useState<boolean>(false);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<number | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [selectedSubGroupIndex, setSelectedSubGroupIndex] = useState<number | null>(null);
  const tabViewRef = useRef<TabView>(null);
  const menu = useRef<Menu>(null);
  const { dagId, taskId } = useParams();
  const { mutate: updateTask, isPending } = useUpdateTask();

  const displayTimezone = useDisplayTimezone();

  const isTemplateForced = taskCfg.templateInfo.type === "PREDEFINED";
  const isTemplated = taskCfg.templateInfo.type === "USER_CUSTOM" || isTemplateForced;

  const menuItems = [
    {
      label: T("action.create-dataset"),
      icon: "pi pi-plus-circle",
      command: () => setCreateDialogVisibility(true),
    },
    {
      label: T("action.rollback-to"),
      icon: "pi pi-history",
      command: () => setShowRollbackDialog(true),
    },
    {
      label: T("word.template-indo"),
      icon: "pi pi-info-circle",
      command: () => setShowTemplateInfoDialog(true),
    },
    {
      label: T("action.update-template"),
      icon: "pi pi-refresh",
      command: () => setShowTemplateUpdateDialog(true),
    },
  ];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dag = dagId ?? params.get("dagId");
    const task = taskId ?? params.get("taskId");

    if (task == null || dag == null) {
      throw Error(T("error.invalid-task-edito-url-options"));
    }

    ScheduledTasksApi.getAirflowTaskConfig(dag, task).then(r => {
      if (!r.error) {
        setTaskCfg(r.data!!);
        setTaskCfgLoading(false);
      } else {
        setTaskCfgLoading(false);
        console.log(`${T("word.error")}${r.error}`);
      }
    });
  }, []);

  function setDefinition(e: TaskTemplateDefinitionDTO) {
    const newCfg = new ScheduledTaskDTO(taskCfg);
    newCfg.templateInfo.definition = e;
    setTaskCfg(newCfg);
  }

  const setInputGroups = useCallback(
    (e: Record<string, TaskInputGroup[]>) => {
      const newCfg = new ScheduledTaskDTO(taskCfg);
      newCfg.inputGroups = e;
      setTaskCfg(newCfg);
    },
    [taskCfg]
  );

  const setInputPropertyState = (property: keyof ScheduledTaskDTO, value: any) => {
    setTaskCfg(
      prev =>
        new ScheduledTaskDTO({
          ...prev,
          [property]: value,
        } as any)
    );
  };

  const handleBasicInputChange = (e: InputChangeEvent<any>) => {
    setInputPropertyState(e.property as keyof ScheduledTaskDTO, e.value);
  };

  const toggleIsUserTemplated = (e: InputChangeEvent<boolean>) => {
    const type: TaskTemplatingType = e.value ? "USER_CUSTOM" : "NONE";
    const newCfg = new ScheduledTaskDTO(taskCfg);
    newCfg.templateInfo.type = type;
    setTaskCfg(newCfg);
  };

  const handleJsonEditorChange = (e: string | undefined) => {
    setInputPropertyState("freestyleJson", e);
  };

  const handleCodeFileChange = (e: IPythonCodeFile) => {
    setInputPropertyState("codeFileId", e.id);
    setInputPropertyState("codeFileName", e.name);
    setInputPropertyState("code", e.code);
    setInputPropertyState("codeB64", e.codeB64);
  };

  const handleTemplateGroupsChange = (e: Record<string, TaskInputGroup[]>) => {
    setInputPropertyState("inputGroups", e);
  };

  const openAirflowTab = () => {
    ScheduledTasksApi.getAirflowTaskGraphLink(taskCfg?.parentId!!, taskCfg?.id!!).then(r => {
      if (!r.error) {
        window.open(r.data!!, "_blank");
      } else {
        errorToaster(T("error.error-while-opening-airflow-graph-window-maybe-not-using-sso-session"));
      }
    });
  };

  const handleSave = (run: boolean, onSaved?: () => void) => {
    if (!taskCfg?.title.trim()) {
      errorToaster(T("message.task-title-is-required"));
      return;
    }

    updateTask(
      { config: taskCfg, run },
      {
        onSuccess: () => {
          if (onSaved != null) {
            setTimeout(onSaved, 1000);
          }
        },
      }
    );
  };

  const handleRollback = () => {
    if (selectedTaskHistory == null) {
      warningToaster(T("alert.please-select-a-task-history-to-rollback-to"));
      return;
    }

    const parsedConfig = JSON.parse(selectedTaskHistory?.config);
    const cfg = new ScheduledTaskDTO(parsedConfig);

    setTaskCfg(cfg);
    setShowRollbackDialog(false);
  };

  const onClickSave = () => {
    handleSave(false, onClickGoBack);
  };

  const onClickApply = () => {
    handleSave(false);
  };

  const onClickRun = () => {
    handleSave(true);
  };

  const onClickGoBack = () => {
    // eslint-disable-next-line no-restricted-globals
    history.back();
  };

  const rollbackDialogFooter = () => {
    return (
      <div className="flex justify-content-between">
        <Button label={T("action.confirm-rollback")} icon="pi pi-check" onClick={handleRollback} />
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={() => setShowRollbackDialog(false)}
          className="p-button-danger"
        />
      </div>
    );
  };

  return (
    <div className="flex flex-row justify-content-center">
      <div style={{ width: "1000px", minWidth: "600px" }}>
        <EasasPageTemplateWrapper
          breadcrumb={["Airflow", "Task Config"]}
          title={T("word.scheduled-task-config")}
          cardClass="flex"
          cardStyle={{
            maxWidth: "1100px",
            margin: "auto",
            height: "calc(100% - 1rem)",
          }}
        >
          <Tooltip id="task-title-tooltip" place="top" float />
          <div
            className="flex flex-column justify-content-between python-task-config"
            style={{ width: "1000px", minWidth: "550px" }}
          >
            <div>
              <div className="flex justify-content-between">
                <h2
                  style={{ margin: "0 !important" }}
                  data-tooltip-id="task-title-tooltip"
                  data-tooltip-content={taskCfg.title}
                  className="align-self-center overflow-hidden text-overflow-ellipsis white-space-nowrap"
                >
                  {taskCfg.title}
                </h2>
                {isTemplateForced && (
                  <Tag style={{ fontSize: "1rem" }} className="ml-4 align-self-center">
                    {taskCfg.templateInfo.templateId}:{taskCfg.templateInfo.templateVersion}
                  </Tag>
                )}
                <div className="flex align-items-center flex-nowrap white-space-nowrap gap-2">
                  {taskCfg.lastUpdatedBy && (
                    <span className="flex align-items-center mr-4">
                      <span className="flex flex-column align-items-end pr-2 text-sm">
                        <span>{T("word.last-edited-at")}</span>
                        <span>{fmtDatetime(taskCfg.lastUpdated, displayTimezone)}</span>
                      </span>

                      <UserAvatar username={taskCfg.lastUpdatedBy} />
                    </span>
                  )}

                  <TaskDataDownloadButton taskCfg={taskCfg} />

                  <Button onClick={openAirflowTab} icon={<img src={airflowLogo} alt="" />} outlined />

                  <Menu model={menuItems} popup ref={menu} id="popup_menu" />
                  <Button icon="pi pi-ellipsis-v" onClick={menu.current?.toggle} />
                </div>
              </div>

              {taskCfgLoading && <div>{T("word.loading")}</div>}

              {!taskCfgLoading && (
                <TabView
                  activeIndex={activeTabIndex}
                  onTabChange={e => setActiveTabIndex(e.index)}
                  panelContainerStyle={{ paddingTop: "0" }}
                  ref={tabViewRef}
                >
                  <TabPanel header={T("word.general")} contentClassName="mt-3">
                    <div className="p-fluid grid formgrid">
                      <div className="col-12">
                        <h4>{T("word.options")}</h4>
                        <hr />
                      </div>

                      <TextFieldInput label={T("word.task-id")} property="id" value={taskCfg?.id} widthMd={4} disabled />

                      <TextFieldInput label="DAG Id" property="parentId" value={taskCfg?.parentId} widthMd={4} disabled />

                      <TextFieldInput
                        label={T("word.company-identifier")}
                        property="companyRef"
                        value={taskCfg?.companyRef}
                        widthMd={4}
                        disabled
                      />

                      <TextFieldInput
                        label={T("word.title")}
                        property="title"
                        value={taskCfg?.title}
                        invalid={!taskCfg?.title.trim()}
                        widthMd={4}
                        onChange={handleBasicInputChange}
                      />

                      <TextFieldInput
                        label={T("word.description")}
                        info={T("word.tasks-description")}
                        property="description"
                        value={taskCfg?.description}
                        widthMd={8}
                        onChange={handleBasicInputChange}
                      />

                      <NumberFieldInput
                        label={T("word.timeout")}
                        info={T("airflow.timeout-specified-in-seconds-after-this-task-will-be-killed")}
                        property="timeout"
                        value={taskCfg?.timeout}
                        onChange={handleBasicInputChange}
                        widthMd={12}
                        suffix="s"
                      />

                      <TextFieldInput
                        label={T("word.environment-id")}
                        info={T("airflow.environment-to-run-task-in-dependencies")}
                        property="environmentId"
                        value={taskCfg?.environmentId}
                        onChange={handleBasicInputChange}
                        widthMd={4}
                      />

                      <DropdownFieldInput
                        label={T("word.executor-queue")}
                        info={T("airflow.in-which-queue-execute-task-in-queues-have-different-specifications")}
                        property="executorQueue"
                        value={taskCfg?.executorQueue}
                        onChange={handleBasicInputChange}
                        options={taskExecutorQueues}
                        widthMd={4}
                      />

                      <NumberFieldInput
                        label={T("word.queue-slots")}
                        info={T("airflow.slots-taken-in-queue-pool")}
                        property="queueSlots"
                        value={taskCfg?.queueSlots}
                        onChange={handleBasicInputChange}
                        widthMd={4}
                      />

                      <TaskDependencyDropdown
                        label={T("word.downstream-tasks")}
                        info={T("airflow.tasks-that-will-be-executed-after-this-task-successfully-finishes")}
                        property="downstreamTasks"
                        value={taskCfg?.downstreamTasks}
                        onChange={handleBasicInputChange}
                        dagId={taskCfg?.parentId!!}
                      />

                      <BooleanFieldInput
                        label={T("word.skip")}
                        info={T("airflow.skip-this-task-execution")}
                        property="isSkipped"
                        value={taskCfg?.isSkipped}
                        onChange={handleBasicInputChange}
                        widthMd={2}
                      />

                      <BooleanFieldInput
                        label={T("word.include-tokens")}
                        info={T("airflow.include-access-tokens-to-directly-use")}
                        property="includeTokens"
                        value={taskCfg?.includeTokens}
                        onChange={handleBasicInputChange}
                        widthMd={2}
                      />

                      {!isTemplateForced && (
                        <BooleanFieldInput
                          label={T("action.use-template")}
                          info={T("airflow.use-predefined-input-and-output-template")}
                          property="templateInfo"
                          value={isTemplated}
                          onChange={toggleIsUserTemplated}
                          widthMd={2}
                        />
                      )}

                      {!isTemplateForced && (
                        <BooleanFieldInput
                          label={T("action.override-recipients")}
                          info={T("airflow.override-email-recipients-with-extra-recipients")}
                          property="overrideRecipients"
                          value={taskCfg?.overrideRecipients}
                          onChange={handleBasicInputChange}
                          widthMd={2}
                        />
                      )}

                      <BooleanFieldInput
                        label={T("action.allow-edit-locked")}
                        info={T("airflow.allow-editing-locked-fields")}
                        property="allowEditLocked"
                        value={taskCfg?.allowEditLocked}
                        onChange={handleBasicInputChange}
                        widthMd={2}
                      />

                      <BooleanFieldInput
                        label={T("button.strict-data-mode")}
                        info={T("info.strict-data-mode")}
                        property="strictDataMode"
                        value={taskCfg?.strictDataMode}
                        onChange={handleBasicInputChange}
                        widthMd={2}
                      />

                      {!isTemplated && (
                        <BooleanFieldInput
                          label={T("action.use-categories")}
                          info={T("airflow.enable-dataset-category-inputs")}
                          property="useCategories"
                          value={taskCfg?.useCategories}
                          onChange={handleBasicInputChange}
                          widthMd={2}
                        />
                      )}

                      <div className={`col-${6}`} />

                      {taskCfg?.overrideRecipients && (
                        <ChipsFieldInput
                          label={T("word.extra-recipients")}
                          info={T("airflow.extra-execution-report-email-recipients")}
                          property="extraExecutionRecipients"
                          value={taskCfg?.extraExecutionRecipients}
                          onChange={handleBasicInputChange}
                          widthMd={12}
                        />
                      )}

                      {!isTemplateForced && (
                        <CodeUploadInput
                          value={taskCfg?.codeFileId}
                          initialCode={taskCfg?.code}
                          codeB64={taskCfg?.codeB64}
                          initialFilename={taskCfg?.codeFileName}
                          onChange={handleCodeFileChange}
                        />
                      )}

                      <div className="col-12 mt-3">
                        <h4>{T("word.timing-options")}</h4>
                        <hr />
                      </div>

                      <TimespanFieldInput
                        label={T("load-conf.lag-time")}
                        info={T("airflow.lag-amount-before-time-reference-point")}
                        property="dataLag"
                        propertyUnit="dataLagUnit"
                        value={taskCfg?.dataLag}
                        valueUnit={taskCfg?.dataLagUnit}
                        onChange={handleBasicInputChange}
                        widthMd={6}
                      />

                      <TimespanFieldInput
                        label={T("load-conf.lead-time")}
                        info={T("airflow.lead-amount-after-time-reference-point")}
                        property="dataLead"
                        propertyUnit="dataLeadUnit"
                        value={taskCfg?.dataLead}
                        valueUnit={taskCfg?.dataLeadUnit}
                        onChange={handleBasicInputChange}
                        widthMd={6}
                      />

                      <NumberFieldInput
                        label={T("load-conf.step-count")}
                        info={T("airflow.steps-calculation-will-execute")}
                        property="calculationSteps"
                        value={taskCfg?.calculationSteps}
                        onChange={handleBasicInputChange}
                        widthMd={4}
                      />

                      <TimespanFieldInput
                        label={T("word.step-period")}
                        info={T("airflow.single-calculation-period-steps-width")}
                        property="calculationPeriod"
                        propertyUnit="calculationPeriodUnit"
                        value={taskCfg?.calculationPeriod}
                        valueUnit={taskCfg?.calculationPeriodUnit}
                        onChange={handleBasicInputChange}
                        widthMd={4}
                      />

                      <TimespanFieldInput
                        label={T("word.calculation-offset")}
                        info={T("airflow.offset-from-current-time-at-which-calculation-periods-starts")}
                        property="calculationTimeOffset"
                        propertyUnit="calculationTimeOffsetUnit"
                        value={taskCfg?.calculationTimeOffset}
                        valueUnit={taskCfg?.calculationTimeOffsetUnit}
                        onChange={handleBasicInputChange}
                        widthMd={4}
                      />
                    </div>
                  </TabPanel>

                  {!isTemplated && (
                    <TabPanel header={T("word.generic-data")} disabled={isTemplated} contentClassName="mt-3">
                      <TaskConfigConstantMapTable
                        property="constants"
                        constants={taskCfg?.constants}
                        onChange={handleBasicInputChange}
                      />
                      <TaskConfigProductTemplatesTable
                        header={T("word.product-templates")}
                        productTemplates={taskCfg?.productTemplates}
                        onChange={handleBasicInputChange}
                        bodyClass="mt-5"
                      />

                      <div className="p-fluid grid formgrid mt-3">
                        <div className="col-12">
                          <h4>{T("word.freestyle")} JSON</h4>
                          <hr />
                        </div>

                        <div className="mx-2" style={{ width: "100%" }}>
                          <Editor
                            height="400px"
                            value={taskCfg?.freestyleJson || ""}
                            onChange={e => handleJsonEditorChange(e)}
                            defaultLanguage="json"
                            theme="vs-dark"
                          />
                        </div>
                      </div>
                    </TabPanel>
                  )}

                  {!isTemplated && (
                    <TabPanel header={T("word.named-datasets")} disabled={isTemplated} contentClassName="mt-3">
                      <div className="p-fluid grid formgrid">
                        <div className="col-12">
                          <TaskConfigNamedDatasetTable
                            header={T("word.input-named-datasets")}
                            property="namedInputDatasets"
                            datasets={taskCfg?.namedInputDatasets!!}
                            onChange={handleBasicInputChange}
                          />
                          <TaskConfigNamedDatasetTable
                            header={T("word.output-named-datasets")}
                            property="namedOutputDatasets"
                            datasets={taskCfg?.namedOutputDatasets!!}
                            onChange={handleBasicInputChange}
                            bodyClass="mt-5"
                          />
                        </div>
                      </div>
                    </TabPanel>
                  )}

                  {!isTemplated && (
                    <TabPanel header={T("word.indexed-datasets")} contentClassName="mt-3">
                      <div className="p-fluid grid formgrid">
                        <div className="col-12">
                          <TaskConfigIndexedDatasetTable
                            header={T("word.input-indexed-datasets")}
                            property="indexedInputDatasets"
                            datasets={taskCfg?.indexedInputDatasets!!}
                            onChange={handleBasicInputChange}
                          />
                          <TaskConfigIndexedDatasetTable
                            header={T("word.output-indexed-datasets")}
                            property="indexedOutputDatasets"
                            datasets={taskCfg?.indexedOutputDatasets!!}
                            onChange={handleBasicInputChange}
                            bodyClass="mt-5"
                          />
                        </div>
                      </div>
                    </TabPanel>
                  )}

                  {taskCfg?.useCategories && !isTemplated && (
                    <TabPanel header={T("word.categories")} disabled={isTemplated}>
                      <TaskConfigCategoryTable
                        header={T("word.input-categories")}
                        property="inputCategories"
                        categories={taskCfg?.inputCategories!!}
                        onChange={handleBasicInputChange}
                      />
                      <TaskConfigCategoryTable
                        header={T("word.output-categories")}
                        property="outputCategories"
                        categories={taskCfg?.outputCategories!!}
                        onChange={handleBasicInputChange}
                        bodyClass="mt-5 mb-5"
                      />

                      <div className="p-fluid grid formgrid">
                        <div className="col-12">
                          <h4>{T("word.timing-options")}</h4>
                          <hr />
                        </div>

                        <TimespanFieldInput
                          label={T("word.category-lead-time")}
                          info={T(
                            "airflow.lead-amount-after-time-reference-point-applies-only-for-datasets-from-categories-selection"
                          )}
                          property="categoryDataLead"
                          propertyUnit="categoryDataLeadUnit"
                          value={taskCfg?.categoryDataLead}
                          valueUnit={taskCfg?.categoryDataLeadUnit}
                          onChange={handleBasicInputChange}
                          widthMd={6}
                        />

                        <TimespanFieldInput
                          label={T("word.category-lag-time")}
                          info={T(
                            "airflow.lag-amount-after-time-reference-point-applies-only-for-datasets-from-categories-selection"
                          )}
                          property="categoryDataLag"
                          propertyUnit="categoryDataLagUnit"
                          value={taskCfg?.categoryDataLag}
                          valueUnit={taskCfg?.categoryDataLagUnit}
                          onChange={handleBasicInputChange}
                          widthMd={6}
                        />
                      </div>
                    </TabPanel>
                  )}

                  {/* Render templated tabs */}

                  <TabPanel header={T("word.groups")} disabled={!isTemplated} contentClassName="mt-3">
                    <TabView
                      activeIndex={selectedTemplateIndex ?? 0}
                      onTabChange={e => setSelectedTemplateIndex(e.index)}
                      scrollable
                    >
                      {renderTemplates(taskCfg, selectedSubGroupIndex, handleTemplateGroupsChange)}
                    </TabView>
                  </TabPanel>
                  {/* {isTemplated && renderTemplates(taskCfg, handleTemplateGroupsChange)} */}

                  <TabPanel header={`${T("word.task-config")} JSON`} contentClassName="mt-3">
                    <TaskCfgJSONViewer taskCfg={taskCfg!!} setInputGroups={setInputGroups} />
                  </TabPanel>

                  <TabPanel header={T("word.template-editor")} contentClassName="mt-3">
                    <TaskTemplateDefinitionEditor taskCfg={taskCfg} onChange={e => setDefinition(e)} />
                  </TabPanel>
                </TabView>
              )}
            </div>

            <div className="flex">
              <Button
                label={T("action.save")}
                className="component-editor-button icon-green border-green flex-grow-1 m-2"
                icon="pi pi-save"
                iconPos="right"
                onClick={onClickSave}
                disabled={isPending}
              />
              <Button
                label={T("action.apply")}
                className="component-editor-button icon-blue border-blue flex-grow-1 m-2"
                icon="pi pi-check"
                iconPos="right"
                onClick={onClickApply}
                disabled={isPending}
              />
              <Button
                label={T("action.apply-and-run")}
                className="component-editor-button icon-orange border-orange flex-grow-1 m-2"
                icon="pi pi-play"
                iconPos="right"
                onClick={onClickRun}
                disabled={isPending}
              />
              <Button
                label={T("word.cancel")}
                className="component-editor-button icon-red border-red flex-none m-2"
                icon="pi pi-times"
                onClick={onClickGoBack}
                disabled={isPending}
              />
            </div>
          </div>

          <Dialog
            style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
            header={T("action.create-new-dataset")}
            visible={createDatasetDialogVisibility}
            onHide={() => setCreateDialogVisibility(false)}
            resizable
          >
            <CreateDatasetDialog closeDialog={() => setCreateDialogVisibility(false)} />
          </Dialog>
          <Dialog
            header={T("action.rollback-to")}
            visible={showRollbackDialog}
            style={{ width: "min(450px, 50vw)" }}
            footer={rollbackDialogFooter}
            onHide={() => setShowRollbackDialog(false)}
          >
            <div className="p-fluid grid formgrid">
              <div className="field col-12">
                <ScheduledTaskHistoryTable
                  onChange={taskHistory => setSelectedTaskHistory(taskHistory)}
                  currentConfig={JSON.stringify(taskCfg)}
                />
              </div>
            </div>
          </Dialog>

          <TemplateInfoDialog taskCfg={taskCfg!!} visible={showTemplateInfoDialog} setVisible={setShowTemplateInfoDialog} />
          <TemplateUpdateDialog
            taskCfg={taskCfg!!}
            visible={showTemplateUpdateDialog}
            setVisible={setShowTemplateUpdateDialog}
            onUpdate={() => null}
          />
        </EasasPageTemplateWrapper>
      </div>

      <div className="m-3" style={{ maxWidth: "250px" }}>
        {renderTemplatesInColumn(taskCfg, setSelectedTemplateIndex, setActiveTabIndex, setSelectedSubGroupIndex, tabIndex => {
          const tabContentRef = tabViewRef.current;
          if (tabContentRef) {
            const tabPanel = (tabContentRef.props.children as React.ReactElement<any>[])[activeTabIndex];
            if (tabPanel && tabPanel.props.children.props.setTabIndex) {
              tabPanel.props.children.props.setTabIndex(tabIndex);
            }
          }
        })}
      </div>
    </div>
  );
}

function TaskTemplateDefinitionEditor(props: { taskCfg: ScheduledTaskDTO; onChange?: (e: any) => void }) {
  const [def, setDef] = useState<string | undefined>(JSON.stringify(props.taskCfg.templateInfo.definition, null, 2));
  const handleDefChange = (newDef: any) => {
    try {
      const val = JSON.parse(newDef);
      props.onChange && props.onChange(val);
      easasAlert({
        summary: T("alert.success"),
        detail: T("alert.template-definition-applied"),
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      easasAlert({
        summary: T("alert.error"),
        detail: T("alert.invalid-template-definition-json"),
        severity: "error",
      });
    }
  };

  return (
    <div className="p-fluid grid formgrid">
      <div className="field col-12">
        <TabView activeIndex={0}>
          <TabPanel header={T("word.basic-editor")}>
            <TemplateEditor
              initialData={def}
              handleDefChange={e => {
                const newDef = JSON.stringify(e, null, 2);
                setDef(newDef);
                handleDefChange(newDef);
              }}
            />
          </TabPanel>
          <TabPanel header={T("word.json-editor")}>
            <TaskTemplateDefinitionEditorJSON
              taskCfg={props.taskCfg}
              onChange={e => {
                setDef(JSON.stringify(e, null, 2));
                handleDefChange(JSON.stringify(e, null, 2));
              }}
            />
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
}

function TaskTemplateDefinitionEditorJSON(props: { taskCfg: ScheduledTaskDTO; onChange?: (e: any) => void }) {
  const [def, setDef] = useState<string | undefined>(JSON.stringify(props.taskCfg.templateInfo.definition, null, 2));

  const handleDefChange = () => {
    try {
      const val = JSON.parse(def!!);
      props.onChange!!(val);
      easasAlert({
        summary: T("alert.success"),
        detail: T("alert.template-definition-applied"),
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      easasAlert({
        summary: T("alert.error"),
        detail: T("alert.invalid-template-definition-json"),
        severity: "error",
      });
    }
  };

  return (
    <div className="p-fluid grid formgrid">
      <div className="field col-12">
        <Editor height="650px" defaultLanguage="json" value={def} onChange={e => setDef(e)} theme="vs-dark" />
      </div>

      <div className="field col-12">
        <Button label={T("action.apply-template")} className="p-button-success" icon="pi pi-check" onClick={handleDefChange} />
      </div>
    </div>
  );
}

function TaskCfgJSONViewer(props: {
  taskCfg: ScheduledTaskDTO;
  setInputGroups: (e: Record<string, TaskInputGroup[]>) => void;
}) {
  const [isInputGroupsJSONEditorVisible, setIsInputGroupsJSONEditorVisible] = useState(false);
  const cfg = { ...props.taskCfg };
  cfg.code = undefined;

  return (
    <div className="flex flex-column gap-3">
      <PermissionGuard permissions="HIDDEN_FEATURE_ACCESS">
        <Button
          label={T("label.edit-input-groups-json")}
          className="component-editor-button icon-green w-max"
          icon="pi pi-check"
          onClick={() => setIsInputGroupsJSONEditorVisible(true)}
        />
      </PermissionGuard>
      <JSONEditor
        config={props.taskCfg.inputGroups}
        onInputGroupsChange={props.setInputGroups}
        isVisible={isInputGroupsJSONEditorVisible}
        setIsVisible={setIsInputGroupsJSONEditorVisible}
        mode={JSONEditorMode.INPUT_GROUPS}
      />
      <code style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(cfg, null, 2)}</code>
    </div>
  );
}

interface ITemplatedTaskTabContentProps {
  groupDef: TaskTemplateInputGroupDefinition;
  groups: TaskInputGroup[];
  taskCfg: ScheduledTaskDTO;
  onChange?: (e: TaskInputGroup[]) => void;
}
const renderTemplates = (
  taskCfg: ScheduledTaskDTO,
  subGroupTabIndex?: number | null,
  onGroupsChanged?: (e: Record<string, TaskInputGroup[]>) => void
) => {
  const defs = taskCfg?.templateInfo.definition?.inputGroupDefinitions;

  const onChange = (groupName: string, groups: TaskInputGroup[]) => {
    const updated = taskCfg.inputGroups!!;
    updated[groupName] = groups;
    onGroupsChanged!!(updated);
  };

  return (
    defs &&
    defs.map(groupDef => {
      const groups = taskCfg.inputGroups[groupDef.id] ?? [];

      return (
        <TabPanel key={groupDef.id} header={groupDef.header}>
          <TemplatedTaskTabContent
            groupDef={groupDef}
            groups={groups}
            taskCfg={taskCfg}
            onChange={currentGroups => onChange(groupDef.id, currentGroups)}
            subGroupTabIndex={subGroupTabIndex ?? 0}
          />
        </TabPanel>
      );
    })
  );
};

const CustomListItem = ({
  item,
  index,
  setSelectedTemplateIndex,
  setActiveTabIndex,
  setTabIndex,
  setSelectedSubGroupIndex,
}: CustomListItemProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleItemClick = () => {
    if (item.subGroups && item.subGroups.length > 0) {
      setIsExpanded(!isExpanded);
    } else {
      setSelectedTemplateIndex(index);
      setActiveTabIndex(5);
    }
  };

  const handleSubGroupChange = (templateIndex: number, subGroupIndex: number) => {
    setSelectedTemplateIndex(templateIndex);
    setSelectedSubGroupIndex(subGroupIndex);
    setActiveTabIndex(5);
    setTabIndex(subGroupIndex + 1);
  };

  return (
    <div className="list-group-container">
      <div
        className="list-group-label-container"
        onClick={handleItemClick}
        onMouseEnter={e => {
          const target = e.currentTarget;
          target.style.backgroundColor = "#f3f4f6";
        }}
        onMouseLeave={e => {
          const target = e.currentTarget;
          target.style.backgroundColor = "#fff";
        }}
      >
        {item.subGroups && item.subGroups.length > 0 && (
          <i className={`pi pi-chevron-${isExpanded ? "down" : "right"}`} style={{ fontSize: "1.2rem" }} />
        )}
        <span className="list-group-label">{item.label}</span>
      </div>
      {isExpanded && item.subGroups && item.subGroups.length > 0 && (
        <div style={{ borderRadius: "5px" }}>
          {item.subGroups.map((subGroup: TaskInputGroup, subIndex: number) => (
            <div
              key={subGroup.title}
              className="expanded-group-container"
              onClick={() => handleSubGroupChange(index, subIndex)}
              onMouseEnter={e => {
                const target = e.currentTarget;
                target.style.backgroundColor = "#f3f4f6";
              }}
              onMouseLeave={e => {
                const target = e.currentTarget;
                target.style.backgroundColor = "#fff";
              }}
            >
              {subGroup.title ?? T("message.subgroup-is-not-defined")}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
const renderTemplatesInColumn = (
  taskCfg: ScheduledTaskDTO,
  setSelectedTemplateIndex: (e: number | null) => void,
  setActiveTabIndex: (e: number) => void,
  setSelectedSubGroupIndex: (e: number | null) => void,
  setTabIndex: (e: number) => void
) => {
  const defs = taskCfg?.templateInfo.definition?.inputGroupDefinitions;
  const groupsOptions =
    defs?.map((def, index) => ({
      label: def.header,
      value: def.id,
      type: def.type,
      subGroups: def.type === "GROUPED" ? taskCfg.inputGroups[def.id] : [],
      index,
    })) ?? [];

  return (
    <div>
      <div className="mb-2">{T("word.groups")}</div>
      <div style={{ boxShadow: "0 2px 12px 0 rgba(0, 0, 0, 0.06)" }}>
        {groupsOptions.map(item => (
          <CustomListItem
            key={item.value}
            item={item}
            index={item.index}
            setSelectedTemplateIndex={setSelectedTemplateIndex}
            setActiveTabIndex={setActiveTabIndex}
            setSelectedSubGroupIndex={setSelectedSubGroupIndex}
            setTabIndex={setTabIndex}
          />
        ))}
      </div>
    </div>
  );
};

export const TemplatedTaskTabContent = (props: ITemplatedTaskTabContentProps & { subGroupTabIndex?: number }) => {
  const [tabIndex, setTabIndex] = useState(props.subGroupTabIndex ?? 0);

  useEffect(() => {
    if (props.subGroupTabIndex !== undefined) {
      setTabIndex(props.groups.length > 0 ? props.subGroupTabIndex + 1 : 0);
    }
  }, [props.subGroupTabIndex]);

  const def = props.groupDef;
  const { subForms } = def;
  const { groups } = props;

  const tabIndexChangeHandler = (title: string) => {
    setTabIndex(groups.map(e => e.title).indexOf(title) + 1);
  };

  const onChange = (inputType: TaskTemplateInputType, subgroupIndex: number, e: InputChangeEvent<any>) => {
    const prop = e.property as keyof TaskInputGroup;
    const valueLoc = TaskInputGroup.getValueLocation(inputType);
    if (!valueLoc) return;

    let subgroup = groups[subgroupIndex];

    // Make sure to initialize subgroup object if it doesn't exist
    if (!subgroup) {
      subgroup = new TaskInputGroup({
        isGrouped: def.type === TaskTemplateInputGroupType.GROUPED,
      });
      groups[subgroupIndex] = subgroup;
    }

    // Initialize task group map object if it doesn't exist
    if (subgroup[valueLoc] == null) {
      (subgroup[valueLoc] as unknown as Record<string, any>) = {};
    }

    (subgroup[valueLoc] as Record<string, any>)[prop] = e.value;

    props.onChange!!(groups);
  };

  const onGroupsChange = (e: InputChangeEvent<TaskInputGroup[]>) => {
    props.onChange!!(e.value);
  };

  /**
   * Get value of input field from group structure
   * @param inputName Reference name of input field
   * @param input Input field definition
   * @param subgroupIndex index of subgroup, 0 if not grouped
   */
  const getFieldValue = (inputName: string, input: ITaskTemplateInputDefinition, subgroupIndex: number = 0) => {
    const valueLoc = TaskInputGroup.getValueLocation(input.type);
    if (!valueLoc) return undefined;

    const subgroup = groups[subgroupIndex];

    if (!subgroup) return null; // No subgroup, no value

    const inputTypeGroup = subgroup[valueLoc] as Record<string, any>; // Get input type group (e.g. "constants") input map
    if (!inputTypeGroup) return null;

    return inputTypeGroup[inputName];
  };

  const groupForm = (subgroupIndex: number = 0) => {
    return (
      subForms?.length &&
      subForms.map((subForm, subFormIdx) => {
        return (
          <div className="p-fluid grid formgrid" key={subFormIdx}>
            {/* ======= SubForm Header ====== */}
            <div
              style={{
                marginTop: def.type === TaskTemplateInputGroupType.SIMPLE ? "1rem" : "",
              }}
              className="col-12"
            >
              {subForm.header && (
                <h4>
                  {subForm.header}
                  <div className="inline-block">
                    <CopyTextWithButton text="" copyableText={subForm.header} />
                  </div>
                </h4>
              )}
              <hr />
            </div>

            {/* ======= Group form input mapping ====== */}
            {subForm?.inputs &&
              subForm?.inputs.map(input => {
                return (
                  <TemplatedTaskInputField
                    key={input.id}
                    inputName={input.id}
                    input={input}
                    value={getFieldValue(input.id, input, subgroupIndex)}
                    onChange={e => onChange(input.type, subgroupIndex, e)}
                    allowEditLocked={props.taskCfg.allowEditLocked}
                  />
                );
              })}
          </div>
        );
      })
    );
  };

  return (
    <div>
      {def.type === TaskTemplateInputGroupType.GROUPED && (
        <TabView scrollable activeIndex={tabIndex} onTabChange={e => setTabIndex(e.index)}>
          <TabPanel leftIcon="pi pi-cog">
            <TaskTemplatesSubgroupManagerTable subgroups={groups} onChange={onGroupsChange} changeTab={tabIndexChangeHandler} />
          </TabPanel>

          {groups.map((group, subgroupIndex) => {
            return (
              <TabPanel key={group.title} header={group.title}>
                {groupForm(subgroupIndex)}
              </TabPanel>
            );
          })}
        </TabView>
      )}

      {def.type === TaskTemplateInputGroupType.SIMPLE && groupForm()}
    </div>
  );
};

interface ITemplatedTaskInputFieldProps {
  inputName: string;
  input: ITaskTemplateInputDefinition<any>;
  value: any | null;
  onChange: (e: InputChangeEvent<any>) => void;
  allowEditLocked: boolean;
}

function TemplatedTaskInputField(props: ITemplatedTaskInputFieldProps) {
  const { input } = props;

  const onChange = (e: InputChangeEvent<any>) => {
    props.onChange!!(e);
  };

  const commonProps = {
    label: input.title!!,
    info: input.info,
    widthMd: input.width || 12,
    property: props.inputName,
    onChange,
    value: props.value,
    allowEditLocked: props.allowEditLocked,
  } as IFieldInputProps;

  return (
    <>
      {/* ========================= Constants ========================= */}
      {input.type === TaskTemplateInputType.CONSTANT && (
        <TaskConfigConstantField {...commonProps} fieldOptions={input.options} withCopyButton />
      )}

      {/* ========================= Dataset Pairs ========================= */}
      {input.type === TaskTemplateInputType.DATASET_PAIRS && (
        <TaskConfigDatasetPairTable {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Indexed datasets ========================= */}
      {(input.type === TaskTemplateInputType.INPUT_INDEXED_DATASETS ||
        input.type === TaskTemplateInputType.OUTPUT_INDEXED_DATASETS) && (
        <DatasetMultiSelectFieldInput {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Named datasets ========================= */}
      {(input.type === TaskTemplateInputType.INPUT_NAMED_DATASET ||
        input.type === TaskTemplateInputType.OUTPUT_NAMED_DATASET) && (
        <DatasetDropdownFieldInput {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Named Categories ========================= */}
      {(input.type === TaskTemplateInputType.INPUT_NAMED_CATEGORY ||
        input.type === TaskTemplateInputType.OUTPUT_NAMED_CATEGORY) && (
        <DatasetCategoryDropdownField {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Product Templates ========================= */}
      {input.type === TaskTemplateInputType.PRODUCT_TEMPLATE && (
        <TaskConfigProductTemplateField {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Freestyle JSON ========================= */}
      {input.type === TaskTemplateInputType.FREESTYLE_JSON && (
        <TaskConfigFreestyleJsonField {...commonProps} fieldOptions={input.options} withCopyButton />
      )}
      {/* ========================= Comment ================================ */}
      {input.type === TaskTemplateInputType.COMMENT && <InputComment {...props.input} />}

      {/* ========================= Invalid type warning ========================= */}
      {!Object.values(TaskTemplateInputType).includes(input.type) && (
        <div className={`field col-${input.width}`}>
          <InfoLabel info={input.info}>{input.title}</InfoLabel>
          <div style={{ background: "orangered", color: "ivory", padding: "4px" }}>
            {T("message.invalid-input-type")}: {input.type}
          </div>
        </div>
      )}
    </>
  );
}
